import React, { useState, useEffect } from 'react';
import { Globe, Heart, Users, Award, Target, Eye, ChevronLeft, ChevronRight, Mail, Phone, Linkedin, Facebook, Twitter, Instagram } from 'lucide-react';

const AboutPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const teamMembers = [
    {
      name: 'Romen Raihan',
      position: 'Head of Public Relations',
      image: '/Romen Raihan.jpg',
      email: 'romen@madeinbd.com',
      phone: '+88 01770 156 736',
      linkedin: '#',
      facebook: '#',
      twitter: '#',
      instagram: '#'
    },
    {
      name: 'Rifat Raihan',
      position: 'Head of Marketing',
      image: '/Rifat Raihan.jpg',
      email: 'rifat@madeinbd.com',
      phone: '+88 01770 156 736',
      linkedin: '#',
      facebook: '#',
      twitter: '#',
      instagram: '#'
    },
    {
      name: 'Sharker Anin Mehedi',
      position: 'Merchant Acquisition',
      image: '/Sharker Anin Mehedi.jpg',
      email: 'sharker@madeinbd.com',
      phone: '+88 01770 156 736',
      linkedin: '#',
      facebook: '#',
      twitter: '#',
      instagram: '#'
    },
    {
      name: 'Advocate Aslam Mia',
      position: 'Legal and Compliance',
      image: '/Advocate Aslam Mia copy.jpg',
      email: 'aslam@madeinbd.com',
      phone: '+88 01770 156 736',
      linkedin: '#',
      facebook: '#',
      twitter: '#',
      instagram: '#'
    },
    {
      name: 'Hasan Mahmud Zilany',
      position: 'Head of Operations',
      image: '/Hasan Mahmud Zilany.jpeg',
      email: 'hasan@madeinbd.com',
      phone: '+88 01770 156 736',
      linkedin: '#',
      facebook: '#',
      twitter: '#',
      instagram: '#'
    },
    {
      name: 'Md. Shahidul Islam',
      position: 'Head of Overseas Liaison',
      image: '/Md. Shahidul Alam.jpeg',
      email: 'shahidul@madeinbd.com',
      phone: '+88 01770 156 736',
      linkedin: '#',
      facebook: '#',
      twitter: '#',
      instagram: '#'
    },
    {
      name: 'Md. Rasel Mia',
      position: 'Head of Logistics',
      image: '/Md. Rasel Mia.jpg',
      email: 'rasel@madeinbd.com',
      phone: '+88 01770 156 736',
      linkedin: '#',
      facebook: '#',
      twitter: '#',
      instagram: '#'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % teamMembers.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + teamMembers.length) % teamMembers.length);
  };

  // Auto-slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-madeinbd-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Made in BD</h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto">
            Connecting Bangladesh with the world through authentic products and meaningful partnerships
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
              <div className="flex items-start space-x-4">
                <div className="bg-madeinbd-100 p-3 rounded-full">
                  <Target className="h-8 w-8 text-madeinbd-600" />
                </div>
                <div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    To empower Bangladeshi manufacturers and artisans by providing them with a global platform 
                    to showcase their products, connect with international buyers, and grow their businesses 
                    while preserving traditional craftsmanship.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Eye className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    To be the leading marketplace that represents the authentic spirit of Bangladesh, 
                    making "Made in Bangladesh" a symbol of quality, craftsmanship, and cultural richness 
                    recognized worldwide.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Story</h2>
            <p className="text-xl text-gray-600">From a vision to a thriving marketplace</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/about us page.jpg" 
                alt="Bangladeshi artisans crafting traditional products"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
                onError={(e) => {
                  // Fallback to placeholder if image not found
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://images.pexels.com/photos/7148621/pexels-photo-7148621.jpeg';
                }}
              />
            </div>
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Made in BD was born from a simple yet powerful idea: to create a bridge between the rich 
                heritage of Bangladeshi craftsmanship and the global marketplace. Founded by Nur Mohammod, 
                our platform celebrates the creativity, skill, and dedication of Bangladeshi artisans and manufacturers.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                From handwoven textiles that tell stories of generations to innovative products that showcase 
                modern Bangladesh, we curate only the finest products that represent the true spirit of our nation.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Today, we're proud to connect hundreds of sellers with customers worldwide, ensuring that 
                every purchase supports local communities and preserves traditional crafts for future generations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-madeinbd-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-madeinbd-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Authenticity</h3>
              <p className="text-gray-600">
                Every product tells a genuine story of Bangladeshi heritage and craftsmanship
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Quality</h3>
              <p className="text-gray-600">
                We maintain the highest standards to ensure customer satisfaction and trust
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Community</h3>
              <p className="text-gray-600">
                Supporting local artisans and fostering sustainable economic growth
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Global Reach</h3>
              <p className="text-gray-600">
                Bringing Bangladesh to the world while maintaining our cultural identity
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Meet Our Founder</h2>
            <p className="text-xl text-gray-600">The visionary behind Made in BD</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Nur Mohammod</h3>
                  <p className="text-madeinbd-600 font-medium mb-4">Founder & CEO</p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    With a passion for promoting Bangladeshi products globally, Nur founded Made in BD 
                    to create opportunities for local artisans and manufacturers. His vision is to make 
                    "Made in Bangladesh" a globally recognized mark of quality and authenticity.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Through this platform, Nur aims to bridge the gap between traditional craftsmanship 
                    and modern e-commerce, ensuring that the rich heritage of Bangladesh reaches every 
                    corner of the world.
                  </p>
                  
                  {/* Founder Social Media Icons */}
                  <div className="flex space-x-4 mt-6">
                    <a href="mailto:nur@madeinbd.com" className="bg-gray-100 p-3 rounded-full hover:bg-madeinbd-100 transition-colors">
                      <Mail className="h-5 w-5 text-gray-600 hover:text-madeinbd-600" />
                    </a>
                    <a href="tel:+8801770156736" className="bg-gray-100 p-3 rounded-full hover:bg-madeinbd-100 transition-colors">
                      <Phone className="h-5 w-5 text-gray-600 hover:text-madeinbd-600" />
                    </a>
                    <a href="#" className="bg-gray-100 p-3 rounded-full hover:bg-blue-100 transition-colors">
                      <Linkedin className="h-5 w-5 text-gray-600 hover:text-blue-600" />
                    </a>
                    <a href="#" className="bg-gray-100 p-3 rounded-full hover:bg-blue-100 transition-colors">
                      <Facebook className="h-5 w-5 text-gray-600 hover:text-blue-600" />
                    </a>
                    <a href="#" className="bg-gray-100 p-3 rounded-full hover:bg-blue-100 transition-colors">
                      <Twitter className="h-5 w-5 text-gray-600 hover:text-blue-400" />
                    </a>
                    <a href="#" className="bg-gray-100 p-3 rounded-full hover:bg-pink-100 transition-colors">
                      <Instagram className="h-5 w-5 text-gray-600 hover:text-pink-600" />
                    </a>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center p-8">
                  <div className="w-64 h-64 rounded-full overflow-hidden shadow-xl">
                    <img 
                      src="/Nur Mohammod.jpg" 
                      alt="Nur Mohammod - Founder & CEO"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to placeholder if image not found
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Members Slider */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Team</h2>
            <p className="text-xl text-gray-600">Meet the dedicated professionals driving our mission forward</p>
          </div>

          {/* Team Slider */}
          <div className="relative">
            <div className="overflow-hidden rounded-lg">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {teamMembers.map((member, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden mx-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2">
                        <div className="p-8 lg:p-12">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                          <p className="text-madeinbd-600 font-medium mb-6">{member.position}</p>
                          <p className="text-gray-700 leading-relaxed mb-6">
                            A dedicated professional bringing expertise and passion to drive Made in BD's mission of connecting Bangladesh with the global marketplace.
                          </p>
                          
                          {/* Contact & Social Media Icons */}
                          <div className="flex flex-wrap gap-3">
                            <a href={`mailto:${member.email}`} className="bg-gray-100 p-3 rounded-full hover:bg-madeinbd-100 transition-colors group">
                              <Mail className="h-5 w-5 text-gray-600 group-hover:text-madeinbd-600" />
                            </a>
                            <a href={`tel:${member.phone}`} className="bg-gray-100 p-3 rounded-full hover:bg-green-100 transition-colors group">
                              <Phone className="h-5 w-5 text-gray-600 group-hover:text-green-600" />
                            </a>
                            <a href={member.linkedin} className="bg-gray-100 p-3 rounded-full hover:bg-blue-100 transition-colors group">
                              <Linkedin className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
                            </a>
                            <a href={member.facebook} className="bg-gray-100 p-3 rounded-full hover:bg-blue-100 transition-colors group">
                              <Facebook className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
                            </a>
                            <a href={member.twitter} className="bg-gray-100 p-3 rounded-full hover:bg-blue-100 transition-colors group">
                              <Twitter className="h-5 w-5 text-gray-600 group-hover:text-blue-400" />
                            </a>
                            <a href={member.instagram} className="bg-gray-100 p-3 rounded-full hover:bg-pink-100 transition-colors group">
                              <Instagram className="h-5 w-5 text-gray-600 group-hover:text-pink-600" />
                            </a>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center p-8">
                          <div className="w-48 h-48 rounded-full overflow-hidden shadow-lg">
                            <img 
                              src={member.image} 
                              alt={member.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Fallback to placeholder if image not found
                                const target = e.target as HTMLImageElement;
                                target.src = 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg';
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>

            {/* Slide Indicators */}
            <div className="flex justify-center mt-8 space-x-2">
              {teamMembers.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    currentSlide === index 
                      ? 'bg-madeinbd-600 scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-madeinbd-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Join Our Journey</h2>
          <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
            Whether you're a seller looking to showcase your products or a buyer seeking authentic 
            Bangladeshi goods, we invite you to be part of our growing community.
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <button className="bg-white text-madeinbd-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Start Selling
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-madeinbd-600 transition-colors">
              Explore Products
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;